import { Mail, Facebook, Instagram } from "lucide-react";
import { Link } from "react-router-dom";

const SOCIALS = [
  {
    href: "https://www.facebook.com/profile.php?id=61570981194034",
    label: "Facebook",
    testId: "footer-facebook",
    icon: Facebook,
  },
  {
    href: "https://www.instagram.com/compass_kids_sa/",
    label: "Instagram",
    testId: "footer-instagram",
    icon: Instagram,
  },
  {
    href: "https://www.tiktok.com/@compass.kids",
    label: "TikTok",
    testId: "footer-tiktok",
    icon: null, // TikTok not in lucide — use inline SVG below
  },
];

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M19.5 6.6a5.4 5.4 0 0 1-3.2-1V15a5.5 5.5 0 1 1-5.5-5.5c.2 0 .5 0 .7.1v2.7a2.8 2.8 0 1 0 2 2.7V2h2.6a5.4 5.4 0 0 0 3.4 4.6v2z" />
    </svg>
  );
}

export default function Footer() {
  // After navigating via any footer link, scroll the destination page to the
  // top so the viewport isn't still parked at the footer. Needed because
  // react-router-dom preserves scroll position on client-side navigation.
  const handleFooterNav = () => {
    // Defer so the new route renders before we scroll — rAF is enough.
    requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
  };
  return (
    <footer className="bg-forest text-cream/80 mt-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-8 sm:grid-cols-3">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src="/logo.png" alt="Compass Kids logo" className="w-9 h-9 rounded-full object-cover border border-cream/20" />
            <span className="font-heading font-black text-lg text-cream">Compass Kids</span>
          </div>
          <p className="text-sm leading-relaxed">Helping kids meet up, make friends, and learn — homeschoolers and school-goers alike.</p>
        </div>
        <div>
          <h4 className="font-heading font-bold text-cream mb-3 text-sm uppercase tracking-wide">Contact Us</h4>
          <a
            href="mailto:info@compasskids.co.za"
            className="inline-flex items-center gap-2 text-sm hover:text-terracotta transition-colors"
            data-testid="footer-email"
          >
            <Mail className="w-4 h-4" />
            info@compasskids.co.za
          </a>
          <div className="flex items-center gap-3 mt-4">
            {SOCIALS.map((s) => {
              const Icon = s.icon || TikTokIcon;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={s.label}
                  data-testid={s.testId}
                  className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center hover:bg-terracotta hover:border-terracotta hover:text-cream transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>
        <div>
          <h4 className="font-heading font-bold text-cream mb-3 text-sm uppercase tracking-wide">Community</h4>
          <p className="text-sm leading-relaxed">Built for every child who deserves a friend. Every family deserves a village.</p>
        </div>
      </div>
      <div className="border-t border-cream/10 py-4 text-center text-xs text-cream/50">
        <div className="flex items-center justify-center gap-4 mb-2 flex-wrap">
          <Link to="/terms" onClick={handleFooterNav} className="hover:text-terracotta transition-colors underline-offset-2 hover:underline" data-testid="footer-terms-link">
            Terms &amp; Conditions
          </Link>
          <span className="text-cream/30" aria-hidden="true">·</span>
          <Link to="/privacy" onClick={handleFooterNav} className="hover:text-terracotta transition-colors underline-offset-2 hover:underline" data-testid="footer-privacy-link">
            Privacy Policy
          </Link>
          <span className="text-cream/30" aria-hidden="true">·</span>
          <Link to="/safety-rules" onClick={handleFooterNav} className="hover:text-terracotta transition-colors underline-offset-2 hover:underline" data-testid="footer-safety-rules-link">
            Safety Rules
          </Link>
        </div>
        Compass Kids — find your direction, together.
      </div>
    </footer>
  );
}
